import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { cn } from "../../../lib/utils";

const PAGE_SIZE = 10;

export function ReorderAlertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [page, setPage] = useState(1);

  const loadAlerts = useCallback(() => {
    setLoading(true);
    inventoryManagementService
      .getReorderAlerts()
      .then((res) => {
        setAlerts(Array.isArray(res) ? res : []);
        setPage(1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load reorder alerts:", err);
        setAlerts([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const totalPages = Math.max(1, Math.ceil(alerts.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return alerts.slice(start, start + PAGE_SIZE);
  }, [alerts, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const formatQty = (val) => {
    if (val == null || val === "") return "—";
    const n = Number(val);
    return Number.isFinite(n) ? n.toLocaleString() : "—";
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        {
          label: t("inventory.inventoryManagement"),
          onClick: () => navigate("/inventory-management/stock-position"),
        },
        { label: t("inventory.reorderAlerts") },
      ]}
      className="space-y-6 animate-fade-in"
    >
      <PageHeader
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
        title={t("inventory.reorderAlerts")}
        subtitle={t("inventory.reorderAlertsSubtitle")}
      />

      <ContentCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
          <span className="text-xs font-bold text-foreground">
            {t("inventory.reorderAlerts")}
          </span>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map((idx) => (
                <div key={idx} className="h-12 bg-gray-50/50 rounded w-full" />
              ))}
            </div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center space-y-4 max-w-sm mx-auto font-sans">
            <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center shadow-xs">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">
                {t("inventory.noReorderAlerts")}
              </h4>
              <p className="text-xs per-file-hint mt-1.5 leading-relaxed">
                {t("inventory.noReorderAlertsHint")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/inventory-management/stock-position")}
              className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-primary text-white font-bold text-xs hover:bg-accent transition cursor-pointer"
            >
              {t("inventory.returnToStockPosition")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[900px] border-collapse font-sans">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider per-file-hint bg-workspace/30 border-b border-(--color-text-border)/65 font-bold">
                    <th className="px-4 py-3 text-left">{t("inventory.productName")}</th>
                    <th className="px-4 py-3 text-left">{t("inventory.systemName")}</th>
                    <th className="px-4 py-3 text-left">{t("inventory.partNumber")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.totalAvailable")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.salesSafetyStock")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.serviceSafetyStock")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.salesShortageQuantity")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.serviceShortageQuantity")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-medium">
                  {paginatedRows.map((row, index) => {
                    const rowKey = [
                      row.productId,
                      row.partNumber,
                      row.systemName,
                      index,
                    ].join("::");
                    const salesShortage = Number(row.salesShortageQuantity) || 0;
                    const serviceShortage =
                      Number(row.serviceShortageQuantity) || 0;
                    return (
                      <tr
                        key={rowKey}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-4 py-3 font-semibold text-foreground truncate">
                          {row.productName || "—"}
                        </td>
                        <td className="px-4 py-3 per-file-hint truncate">
                          {row.systemName || "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs per-file-hint truncate">
                          {row.partNumber || "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-foreground whitespace-nowrap">
                          {formatQty(row.totalAvailable)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-foreground whitespace-nowrap">
                          {formatQty(row.salesSafetyStock)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-foreground whitespace-nowrap">
                          {formatQty(row.serviceSafetyStock)}
                        </td>
                        <td
                          className={cn(
                            "px-4 py-3 text-right font-mono font-extrabold whitespace-nowrap",
                            salesShortage > 0
                              ? "text-rose-600"
                              : "text-foreground"
                          )}
                        >
                          {formatQty(row.salesShortageQuantity)}
                        </td>
                        <td
                          className={cn(
                            "px-4 py-3 text-right font-mono font-extrabold whitespace-nowrap",
                            serviceShortage > 0
                              ? "text-rose-600"
                              : "text-foreground"
                          )}
                        >
                          {formatQty(row.serviceShortageQuantity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="md:hidden space-y-2.5 p-3">
              {paginatedRows.map((row, index) => {
                const rowKey = [
                  row.productId,
                  row.partNumber,
                  row.systemName,
                  index,
                ].join("::");
                const salesShortage = Number(row.salesShortageQuantity) || 0;
                const serviceShortage =
                  Number(row.serviceShortageQuantity) || 0;
                return (
                  <li
                    key={rowKey}
                    className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {row.productName || "—"}
                      </p>
                      <p className="text-xs per-file-hint mt-0.5 truncate">
                        {row.systemName || "—"}
                      </p>
                      <p className="text-xs font-mono per-file-hint mt-0.5">
                        {row.partNumber || "—"}
                      </p>
                    </div>
                    <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.totalAvailable")}
                        </span>
                        <span className="font-semibold font-mono">
                          {formatQty(row.totalAvailable)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.salesSafetyStock")}
                        </span>
                        <span className="font-semibold font-mono">
                          {formatQty(row.salesSafetyStock)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.serviceSafetyStock")}
                        </span>
                        <span className="font-semibold font-mono">
                          {formatQty(row.serviceSafetyStock)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.salesShortageQuantity")}
                        </span>
                        <span
                          className={cn(
                            "font-extrabold font-mono",
                            salesShortage > 0
                              ? "text-rose-600"
                              : "text-foreground"
                          )}
                        >
                          {formatQty(row.salesShortageQuantity)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.serviceShortageQuantity")}
                        </span>
                        <span
                          className={cn(
                            "font-extrabold font-mono",
                            serviceShortage > 0
                              ? "text-rose-600"
                              : "text-foreground"
                          )}
                        >
                          {formatQty(row.serviceShortageQuantity)}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="px-4 pb-4">
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={alerts.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </ContentCard>
    </AppLayout>
  );
}
